/**
 * Flow cookie consent gate — headless port of the fleet's craft-flow-cookie-consent
 * plugin (v0.7.0). The Craft plugin injects this logic into Craft-rendered pages, which
 * never happens on this site: the visitor-facing app is this Vite build on Netlify, so
 * the gate lives here instead. Same localStorage key and stored-value shape, same POPIA
 * model, so a choice made here means exactly what it means on every other Flow site.
 *
 * The model (analyticsDefault: 'granted'): Google Tag Manager / GA4 run before the
 * visitor has answered the banner — POPIA's legitimate-interest basis with an opt-out.
 * An explicit stored refusal beats the default on every path: consent is denied, GTM is
 * never loaded at all (hard block, not just Consent Mode), and Google cookies are
 * cleared. Marketing is opt-in either way — every ad_* signal stays denied until an
 * explicit accept. This container carries no marketing tags today; the category exists
 * so a tag added to GTM later is still gated by the visitor's stored choice.
 *
 * initConsent() must run before anything else can load a tag — main.jsx calls it where
 * TagManager.initialize used to be.
 */

import TagManager from 'react-gtm-module';

const STORAGE_KEY = 'flow_cookie_consent';
const GTM_ID = 'GTM-W8Q2P3';

/* Cookie-name patterns removed when a category is refused (plugin's clearOnRevoke
   defaults). _ga has a two-year expiry — without this, a visitor who rejects keeps a
   Google identifier on their device indefinitely. */
const CLEAR_ON_REVOKE = {
    analytics: [/^_ga$/, /^_ga_/, /^_gid$/, /^_gat/, /^_clck$/, /^_clsk$/, /^_hj/],
    marketing: [/^_gcl_/, /^_fbp$/, /^_fbc$/, /^li_/, /^bcookie$/, /^lidc$/, /^UserMatchHistory$/, /^_ttp$/, /^_uetsid$/, /^_uetvid$/],
};

const GOOGLE_COOKIE_PATTERNS = ['^_ga$', '^_ga_', '^_gid$', '^_gat'];

window.dataLayer = window.dataLayer || [];
/* Not an arrow function: GTM expects the Arguments object itself on the dataLayer. */
function gtag() { window.dataLayer.push(arguments); }

/* Fail closed. localStorage.getItem itself throws in partitioned or blocked-storage
   contexts, and JSON.parse throws on a corrupt value. If either does, behave as though
   the visitor has not consented — never as though they have. */
function stored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

/* Tags set cookies on the registrable domain, so clear across the host and every parent
   domain, on '/' and the current path. */
function clearCookies(patterns) {
    const host = window.location.hostname;
    const domains = [null, host, '.' + host];
    const parts = host.split('.');
    for (let i = 1; i < parts.length - 1; i++) { domains.push('.' + parts.slice(i).join('.')); }

    document.cookie.split('; ').forEach((pair) => {
        const name = pair.split('=')[0];
        if (!name) { return; }
        if (!patterns.some((re) => re.test(name))) { return; }
        domains.forEach((d) => {
            ['/', window.location.pathname].forEach((p) => {
                document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=' + p + (d ? '; domain=' + d : '');
            });
        });
    });
}

let gtmLoaded = false;
function loadGtm() {
    if (gtmLoaded) { return; }
    gtmLoaded = true;
    TagManager.initialize({ gtmId: GTM_ID });
}

export function initConsent() {
    const choices = stored();
    const analyticsOn = choices ? !!choices.analytics : true;
    const marketingOn = !!(choices && choices.marketing);

    /* Consent Mode v2 defaults, pushed before any tag exists on the page. */
    gtag('consent', 'default', {
        'analytics_storage': analyticsOn ? 'granted' : 'denied',
        'ad_storage': marketingOn ? 'granted' : 'denied',
        'ad_user_data': marketingOn ? 'granted' : 'denied',
        'ad_personalization': marketingOn ? 'granted' : 'denied',
        'wait_for_update': 500,
    });

    /* Cookie hygiene on every load, not only on a change of mind: covers identifiers set
       before this gate existed, and a category revoked in another tab. A choice-less
       visitor keeps their Google cookies — GA is legitimately running for them — but any
       other analytics identifiers must not survive. */
    if (!analyticsOn) {
        clearCookies(CLEAR_ON_REVOKE.analytics);
    } else if (!choices) {
        clearCookies(CLEAR_ON_REVOKE.analytics.filter((re) => GOOGLE_COOKIE_PATTERNS.indexOf(re.source) === -1));
    }
    if (!marketingOn) { clearCookies(CLEAR_ON_REVOKE.marketing); }

    if (analyticsOn) { loadGtm(); }

    /* Same API surface as the plugin's window.flowConsent, so the banner component — and
       anything fleet tooling expects — reads and writes consent through one place. */
    window.flowConsent = {
        stored,

        /* Signal first, then load, so a tag reading consent at init sees the right value
           rather than racing the update. */
        apply(analytics, marketing) {
            gtag('consent', 'update', {
                'analytics_storage': analytics ? 'granted' : 'denied',
                'ad_storage': marketing ? 'granted' : 'denied',
                'ad_user_data': marketing ? 'granted' : 'denied',
                'ad_personalization': marketing ? 'granted' : 'denied',
            });

            if (!analytics) { clearCookies(CLEAR_ON_REVOKE.analytics); }
            if (!marketing) { clearCookies(CLEAR_ON_REVOKE.marketing); }

            if (analytics) { loadGtm(); }
        },

        save(analytics, marketing) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({
                    necessary: true,
                    analytics: !!analytics,
                    marketing: !!marketing,
                    timestamp: new Date().toISOString(),
                }));
            } catch (e) { /* storage blocked: the choice can't persist, but must still apply */ }
            this.apply(!!analytics, !!marketing);
        },

        acceptAll() { this.save(true, true); },
        rejectAll() { this.save(false, false); },
    };
}
